/**
 * loadTripPois — Sprint 1
 * Load the geolocated POIs that drive proximity reveal + whisper for a trip.
 *
 * Two sources, merged:
 *   1. The destination city's POIs (culturalAttractions, monuments, …) from the
 *      active-language country file — these reveal on approach (≤80 m).
 *   2. The country's `quirkyPointsOfInterest` from TRIP/poi/<file>.json — hidden
 *      gems Érico already authored. Marked hidden:true so the neighbourhood
 *      whispers them (≤150 m); their description becomes the revealed note.
 *
 * Best-effort and fully offline-tolerant: any failure on either source yields []
 * for that source. The only coordinates produced live inside the returned list,
 * used solely for in-memory distance maths (never transmitted, never logged).
 *
 * Quirky POIs carry lat/lng, so we don't need to match city names across
 * languages (Roma/Rome) — proximity itself selects which ones can whisper.
 */
import { getCountryFile } from './countryUtils';
import { collectCityPois, poiSlug } from './reveal';
import i18n from '../i18n/index.js';

const slimCity = (s) =>
  String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const langFolder = () => {
  const l = (i18n.language || 'en').split('-')[0];
  return l === 'es' ? 'es/' : l === 'fr' ? 'fr/' : 'en/';
};

const getLocalizedPoiText = (poi, field) => {
  const lang = (i18n.language || 'en').split('-')[0];
  const key = lang === 'es' ? 'Es' : lang === 'fr' ? 'Fr' : '';
  return key ? (poi?.[`${field}${key}`] || poi?.[field]) : poi?.[field];
};

// The poi/ folder names are inconsistent with the country-file ids (spaces vs
// underscores, and some country files carry a `_travel_guide` suffix). Try a few.
const poiCandidates = (fileId) => {
  const base = fileId.replace(/_travel_guide$/, '');
  const set = new Set([
    fileId, fileId.replace(/_/g, ' '), fileId.replace(/\s+/g, '_'),
    base, base.replace(/_/g, ' '), base.replace(/\s+/g, '_'),
  ]);
  return [...set];
};

async function loadCityPois(fileId, city) {
  try {
    const res = await fetch(`./TRIP/${langFolder()}${fileId}.json`);
    if (!res.ok) return [];
    const data = await res.json();
    const target = slimCity(city);
    const cityData = (data.cities || []).find(c => slimCity(c.city) === target);
    if (!cityData) return [];
    return collectCityPois(cityData);
  } catch {
    return [];
  }
}

const flattenQuirkyPois = (data) => {
  const entries = data?.quirkyPointsOfInterest || [];
  const out = [];

  for (const entry of entries) {
    if (Array.isArray(entry?.points)) {
      for (const point of entry.points) {
        out.push({
          ...entry,
          ...point,
          city: entry.city || point.city || '',
        });
      }
      continue;
    }

    out.push(entry);
  }

  return out;
};

async function loadQuirkyPois(fileId) {
  for (const candidate of poiCandidates(fileId)) {
    try {
      const res = await fetch(`./TRIP/poi/${candidate}.json`);
      if (!res.ok) continue;
      const data = await res.json();
      const list = flattenQuirkyPois(data);
      const out = [];
      for (const p of list) {
        if (typeof p.lat !== 'number' || typeof p.lng !== 'number') continue;
        const name = getLocalizedPoiText(p, 'name') || p.name;
        const note = getLocalizedPoiText(p, 'description') || p.description;

        out.push({
          // Identity is ALWAYS the canonical (base) name, never the localized one,
          // so a gem's revealed/surfaced state survives a language switch.
          id: poiSlug(p.name),
          name,
          lat: p.lat,
          lng: p.lng,
          category: 'quirky',
          hidden: true,                              // → the neighbourhood whispers
          reveal: note ? { note } : null,
        });
      }
      return out;
    } catch {
      // try next candidate
    }
  }
  return [];
}

/**
 * Sprint 4A — the hidden gems of ONE specific city (not the whole country).
 *
 * The constellation and the city-postcard reward need to know how many gems a
 * given city holds and which ones, scoped to that city only. Quirky POIs carry a
 * `city` field (Érico tags every gem), so we filter by name — exact and aligned
 * with how loadCityPois selects a city. Returns canonical-id gems: { id, name }.
 *
 * Fully offline-tolerant (local ./TRIP files) and tolerant of a city with no
 * gems → []. Identity is always poiSlug(canonical name), so it matches the ids
 * that the proximity watcher writes into surfacedPois.
 */
export async function loadCityHiddenGems(country, city) {
  try {
    if (!country || !city) return [];
    const fileId = await getCountryFile(country);
    if (!fileId) return [];

    const target = slimCity(city);
    for (const candidate of poiCandidates(fileId)) {
      try {
        const res = await fetch(`./TRIP/poi/${candidate}.json`);
        if (!res.ok) continue;
        const data = await res.json();
        const list = flattenQuirkyPois(data);
        const byId = new Map();
        for (const p of list) {
          if (slimCity(p.city) !== target) continue;
          if (!p.name) continue;
          const id = poiSlug(p.name);
          if (byId.has(id)) continue;
          byId.set(id, { id, name: getLocalizedPoiText(p, 'name') || p.name });
        }
        return [...byId.values()];
      } catch {
        // try next candidate
      }
    }
    return [];
  } catch {
    return [];
  }
}

export async function loadTripPois(trip) {
  try {
    const country = trip?.destination?.country;
    const city = trip?.destination?.city;
    if (!country || !city) return [];

    const fileId = await getCountryFile(country);
    if (!fileId) return [];

    const [cityPois, quirkyPois] = await Promise.all([
      loadCityPois(fileId, city),
      loadQuirkyPois(fileId),
    ]);

    // De-dupe by id; city POIs win if a name collides.
    const byId = new Map();
    for (const p of quirkyPois) byId.set(p.id, p);
    for (const p of cityPois) byId.set(p.id, p);
    return [...byId.values()];
  } catch {
    return [];
  }
}

export default loadTripPois;
